import type { FieldErrors } from "react-hook-form";

import trackEvent from "helpers/trackEvent";
import type { PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { isCommodityRowEmpty, isPackingListRowEmpty } from "@RequestFormV2/sections/PackingList/utils/isRowEmpty";
import { isContainerRow } from "@RequestFormV2/sections/PackingList/utils/packingListSplit";
import type { ReviewFieldErrorSnapshot } from "@RequestFormV2/validation/validateOnReview";
import { getSectionKeyForFieldPath } from "@RequestFormV2/utils/getSectionKeyForFieldPath";

import type { AbandonMethod, FormFlow, FormFlowParams, QuoteFormErrorEntry, RequestFormAnalyticsContext } from "./types";
import { QUOTE_REQUEST_FORM_V2_EVENTS } from "./eventNames";
import { toAnalyticsSectionName } from "./toAnalyticsSectionName";

const DRAFT_VISIT_STORAGE_PREFIX = "rfv2:visit:";

let analyticsContext: RequestFormAnalyticsContext = { formFlow: "new" };
let formStartedAt: number | null = null;
let confirmationStartedAt: number | null = null;
let reviewVisitCount = 0;
let reviewBackToEditingClickCount = 0;
let submittedSuccessfully = false;
let abandonTracked = false;

export function setRequestFormAnalyticsContext(context: RequestFormAnalyticsContext): void {
  analyticsContext = context;
}

export function setFormStartedAt(timestamp: number): void {
  formStartedAt = timestamp;
}

export function getFormStartedAt(): number | null {
  return formStartedAt;
}

export function setConfirmationStartedAt(timestamp: number): void {
  confirmationStartedAt = timestamp;
}

export function getConfirmationStartedAt(): number | null {
  return confirmationStartedAt;
}

export function markFormSubmitted(): void {
  submittedSuccessfully = true;
}

export function wasFormSubmitted(): boolean {
  return submittedSuccessfully;
}

export function resetAbandonTracked(): void {
  abandonTracked = false;
}

/** Resets module-level analytics session state (call on each form session start). */
export function resetRequestFormAnalyticsState(): void {
  analyticsContext = { formFlow: "new" };
  formStartedAt = null;
  confirmationStartedAt = null;
  reviewVisitCount = 0;
  reviewBackToEditingClickCount = 0;
  submittedSuccessfully = false;
  abandonTracked = false;
}

export function getFormFlow({
  isEditQuoteRequest = false,
  isEditShipment = false,
  isQuickRequest = false,
  isEditQuickRequest = false,
}: FormFlowParams): FormFlow {
  if (isEditShipment) return "edit_shipment";
  if (isEditQuoteRequest) return "edit_quote";
  if (isEditQuickRequest) return "edit_quick";
  if (isQuickRequest) return "quick";
  return "new";
}

function baseProperties() {
  return {
    form_version: "v2" as const,
    form_flow: analyticsContext.formFlow,
  };
}

function getElapsedMs(startedAt: number | null): number {
  if (startedAt === null) return 0;
  return Math.max(0, Date.now() - startedAt);
}

function incrementReviewVisitCount(): number {
  reviewVisitCount += 1;
  return reviewVisitCount;
}

function incrementReviewBackClickCount(): number {
  reviewBackToEditingClickCount += 1;
  return reviewBackToEditingClickCount;
}

function getElapsedReviewMs(): number {
  return getElapsedMs(confirmationStartedAt);
}

function getReviewSessionProperties() {
  return {
    review_visit_count: reviewVisitCount,
    review_back_click_count: reviewBackToEditingClickCount,
  };
}

export function incrementDraftVisitCount(draftId: string): number {
  const key = `${DRAFT_VISIT_STORAGE_PREFIX}${draftId}`;
  const current = Number(localStorage.getItem(key) ?? 0);
  const next = current + 1;
  localStorage.setItem(key, String(next));
  return next;
}

/** Counts non-empty packing rows/commodities (filled content only). */
export function getPackingCounts(packingList: PackingListRow[] | undefined) {
  const rows = (packingList ?? []).filter((row) => !isPackingListRowEmpty(row));

  let totalContainers = 0;
  let totalHandlingUnits = 0;
  let totalCommodities = 0;

  rows.forEach((row) => {
    if (isContainerRow(row)) {
      totalContainers += 1;
    } else {
      totalHandlingUnits += 1;
    }

    (row.good_customs_information ?? []).forEach((commodity) => {
      if (!isCommodityRowEmpty(commodity)) {
        totalCommodities += 1;
      }
    });
  });

  return {
    total_containers: totalContainers,
    total_handling_units: totalHandlingUnits,
    total_commodities: totalCommodities,
  };
}

/**
 * Structural row counts after add actions (includes empty rows just appended).
 * Prefer this for add/submit totals so dashboard numbers reconcile.
 */
export function countPackingListRows(packingList: PackingListRow[] | undefined) {
  const rows = packingList ?? [];
  let totalContainers = 0;
  let totalHandlingUnits = 0;
  let totalCommodities = 0;

  rows.forEach((row) => {
    if (isContainerRow(row)) {
      totalContainers += 1;
    } else {
      totalHandlingUnits += 1;
    }
    totalCommodities += (row.good_customs_information ?? []).length;
  });

  return {
    total_containers: totalContainers,
    total_handling_units: totalHandlingUnits,
    total_commodities: totalCommodities,
  };
}

export function flattenReviewErrors(fieldErrors: ReviewFieldErrorSnapshot[]): QuoteFormErrorEntry[] {
  return fieldErrors.map(({ path, error }) => ({
    section_name: getSectionKeyForFieldPath(path) ?? "unknown",
    field_name: path,
    error_type: error.type ?? "validation",
    error_message: error.message ?? "",
  }));
}

export function flattenApiErrors(error: unknown): QuoteFormErrorEntry[] {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== "object") return [];

  const entries: QuoteFormErrorEntry[] = [];

  const visit = (value: unknown, path: string) => {
    if (typeof value === "string") {
      entries.push({
        section_name: getSectionKeyForFieldPath(path) ?? "unknown",
        field_name: path || "unknown",
        error_type: "serverError",
        error_message: value,
      });
      return;
    }

    if (Array.isArray(value)) {
      // DRF-style ["msg"] — record once at the field path; do not recurse into string indices.
      if (value.length > 0 && value.every((item) => typeof item === "string")) {
        entries.push({
          section_name: getSectionKeyForFieldPath(path) ?? "unknown",
          field_name: path || "unknown",
          error_type: "serverError",
          error_message: value[0],
        });
        return;
      }
      value.forEach((item, index) => visit(item, path ? `${path}.${index}` : String(index)));
      return;
    }

    if (!value || typeof value !== "object") return;

    Object.entries(value as Record<string, unknown>).forEach(([key, nestedValue]) => {
      const nextPath = path ? `${path}.${key}` : key;
      visit(nestedValue, nextPath);
    });
  };

  visit(data, "");
  return entries;
}

export function flattenFieldErrors(errors: FieldErrors): QuoteFormErrorEntry[] {
  const entries: QuoteFormErrorEntry[] = [];

  const visit = (value: unknown, path: string) => {
    if (!value || typeof value !== "object") return;

    if ("message" in value || "type" in value) {
      const error = value as { message?: string; type?: string };
      entries.push({
        section_name: getSectionKeyForFieldPath(path) ?? "unknown",
        field_name: path || "unknown",
        error_type: error.type ?? "validation",
        error_message: error.message ?? "",
      });
      return;
    }

    Object.entries(value).forEach(([key, nestedValue]) => {
      const nextPath = path ? `${path}.${key}` : key;
      visit(nestedValue, nextPath);
    });
  };

  visit(errors, "");
  return entries;
}

export function resolveAbandonSectionName(expandedUuids: string[], isReviewMode: boolean): string {
  if (isReviewMode) return "confirmation";
  if (expandedUuids.length === 0) return "request-form.organisation";
  return expandedUuids[expandedUuids.length - 1] ?? "request-form.organisation";
}

/**
 * First section that still looks incomplete after a draft load — used for reentry analytics.
 * Falls back to Organisation when the draft appears complete.
 */
export function resolveFirstActiveSection(formValues: Record<string, unknown>): string {
  const organizational = formValues.organizational as { location?: unknown } | undefined;
  if (!organizational?.location) return "request-form.organisation";

  const service = formValues.service as { service_type?: unknown } | undefined;
  if (!service?.service_type) return "request-form.service";

  const schedule = formValues.schedule as { stops?: unknown[] } | undefined;
  if (!Array.isArray(schedule?.stops) || schedule.stops.length === 0) return "request-form.schedule";

  const packingList = formValues.packing_list as PackingListRow[] | undefined;
  const packingCounts = getPackingCounts(packingList);
  if (packingCounts.total_containers + packingCounts.total_handling_units === 0) {
    return "request-form.packingList";
  }

  const invited = formValues.invited_providers as { providers?: unknown[] } | undefined;
  if (!Array.isArray(invited?.providers) || invited.providers.length === 0) {
    return "request-form.provider";
  }

  return "request-form.organisation";
}

const ABANDON_METHOD_LABELS: Record<AbandonMethod, string> = {
  tab_close: "Tab Close",
  navigate_away: "Navigate Away",
};

function trackAbandonOnce(sectionName: string, abandonMethod: AbandonMethod): void {
  if (abandonTracked || wasFormSubmitted()) return;

  abandonTracked = true;
  trackEvent(QUOTE_REQUEST_FORM_V2_EVENTS.ABANDON_SECTION, {
    ...baseProperties(),
    section_name: toAnalyticsSectionName(sectionName),
    abandon_method: ABANDON_METHOD_LABELS[abandonMethod],
    time_on_form_ms: getElapsedMs(formStartedAt),
    ...getReviewSessionProperties(),
    ...(sectionName === "confirmation" ? { time_on_review_ms: getElapsedReviewMs() } : {}),
  });
}

export function trackQuoteFormEntered(): void {
  trackEvent(QUOTE_REQUEST_FORM_V2_EVENTS.ENTER_FORM, baseProperties());
}

export function trackQuoteTemplateLoaded(templateId: string): void {
  trackEvent(QUOTE_REQUEST_FORM_V2_EVENTS.LOAD_TEMPLATE, {
    ...baseProperties(),
    template_id: templateId,
    section_name: toAnalyticsSectionName("header"),
    template_loaded: true,
  });
}

export function trackQuoteSectionAbandoned(params: {
  section_name: string;
  abandon_method?: AbandonMethod;
  time_on_form_ms?: number;
}): void {
  trackAbandonOnce(params.section_name, params.abandon_method ?? "navigate_away");
}

export function trackFormReentry(params: { draftId: string; firstFieldActive: string }): void {
  trackEvent(QUOTE_REQUEST_FORM_V2_EVENTS.REENTER_DRAFT, {
    ...baseProperties(),
    first_field_active: toAnalyticsSectionName(params.firstFieldActive),
    visit_count: incrementDraftVisitCount(params.draftId),
  });
}

export function trackQuoteDraftSaved(currentSection: string): void {
  trackEvent(QUOTE_REQUEST_FORM_V2_EVENTS.SAVE_DRAFT, {
    ...baseProperties(),
    current_section: toAnalyticsSectionName(currentSection),
  });
}

export function trackQuotePackingHandlingUnitAdded(totalHandlingUnits: number): void {
  trackEvent(QUOTE_REQUEST_FORM_V2_EVENTS.ADD_HANDLING_UNIT, {
    ...baseProperties(),
    total_handling_units: totalHandlingUnits,
  });
}

export function trackQuotePackingContainerAdded(totalContainers: number): void {
  trackEvent(QUOTE_REQUEST_FORM_V2_EVENTS.ADD_CONTAINER, {
    ...baseProperties(),
    total_containers: totalContainers,
  });
}

export function trackQuotePackingCommodityAdded(totalCommodities: number): void {
  trackEvent(QUOTE_REQUEST_FORM_V2_EVENTS.ADD_COMMODITY, {
    ...baseProperties(),
    total_commodities: totalCommodities,
  });
}

export function trackQuoteProviderSelectAll(totalProvidersSelected: number): void {
  trackEvent(QUOTE_REQUEST_FORM_V2_EVENTS.SELECT_ALL_PROVIDERS, {
    ...baseProperties(),
    total_providers_selected: totalProvidersSelected,
  });
}

export function trackQuoteProviderFiltered(params: { filterCount: number; selectFilteredProviderCount: number }): void {
  trackEvent(QUOTE_REQUEST_FORM_V2_EVENTS.FILTER_PROVIDERS, {
    ...baseProperties(),
    filter_count: params.filterCount,
    select_filtered_provider_count: params.selectFilteredProviderCount,
  });
}

export function trackQuoteFormErrorLogged(errors: QuoteFormErrorEntry[]): void {
  if (errors.length === 0) return;

  trackEvent(QUOTE_REQUEST_FORM_V2_EVENTS.LOG_FORM_ERROR, {
    ...baseProperties(),
    error_count: errors.length,
    // Omit error_message — may contain user/business data unsuitable for analytics retention.
    errors: errors.map(({ section_name, field_name, error_type }) => ({
      section_name: toAnalyticsSectionName(section_name),
      field_name,
      error_type,
    })),
  });
}

export function trackQuoteConfirmationViewed(): void {
  trackEvent(QUOTE_REQUEST_FORM_V2_EVENTS.VIEW_CONFIRMATION, {
    ...baseProperties(),
    review_visit_number: incrementReviewVisitCount(),
  });
}

export function trackQuoteConfirmationBackToEditing(): void {
  const timeOnReviewMs = getElapsedReviewMs();

  trackEvent(QUOTE_REQUEST_FORM_V2_EVENTS.BACK_TO_EDITING, {
    ...baseProperties(),
    time_on_confirmation_ms: timeOnReviewMs,
    time_on_review_ms: timeOnReviewMs,
    review_visit_number: reviewVisitCount,
    back_to_editing_click_number: incrementReviewBackClickCount(),
    review_back_click_count: reviewBackToEditingClickCount,
  });

  confirmationStartedAt = null;
}

export function trackQuoteFormSubmitted(params: {
  providerCount: number;
  totalContainers: number;
  totalCommodities: number;
}): void {
  markFormSubmitted();
  const onReviewPage = confirmationStartedAt !== null;

  trackEvent(QUOTE_REQUEST_FORM_V2_EVENTS.SUBMIT_FORM, {
    ...baseProperties(),
    time_to_complete_ms: getElapsedMs(formStartedAt),
    provider_count: params.providerCount,
    total_containers: params.totalContainers,
    total_commodities: params.totalCommodities,
    ...getReviewSessionProperties(),
    ...(onReviewPage ? { time_on_review_ms: getElapsedReviewMs() } : {}),
  });
}

export function trackRouteLeaveAbandon(sectionName: string): void {
  trackAbandonOnce(sectionName, "navigate_away");
}

export function trackTabCloseAbandon(sectionName: string): void {
  trackAbandonOnce(sectionName, "tab_close");
}
